import { fontPluginKey } from '../plugins/FontPlugin';
import { createDefaultFontStyleString } from './fontUtils';
import { HubSpotDOMSerializer } from './domSerializer';

/**
 * Render an EditorState to an HTML string.
 *
 * This class permits overrides and extensions with the same methods
 * as {@link HubSpotDOMSerializer}.
 *
 * Subclasses may override these methods to customize behavior:
 *
 * - `addDefaultModifiers`: add common node or mark modifiers to the
 *   DOM serializer. Use this to change the way specific nodes or
 *   marks are converted to DOM elements.
 * - `prepareContainer`: return any DOM element to be used as the
 *   container for the rendered content. This will replace the
 *   default `<div>` wrapper from the EditorState's root node.
 * - `postprocessDomElement`: mutate the DOM element rendered by the
 *   serializer before it is converted to a string. Use this to
 *   perform cleanup and final transformations using the full DOM
 *   API.
 */
export class EditorStateSerializer extends HubSpotDOMSerializer {
  /**
   * The EditorState attached to this serializer instance. Create a new serializer for each
   * state that needs to be stringified.
   */

  /** Idempotency flag for `addDefaultModifiersOnce`, to avoid duplicate modifiers */

  constructor(editorState) {
    const baseNodeSerializers = EditorStateSerializer.nodesFromSchema(editorState.schema);
    const baseMarkSerializers = EditorStateSerializer.marksFromSchema(editorState.schema);
    super(baseNodeSerializers, baseMarkSerializers);
    this.defaultsAdded = false;
    this.editorState = editorState;
  }

  /**
   * Render our EditorState to an HTML string using the registered modifiers.
   *
   * @returns string
   */
  toHTML() {
    this.addDefaultModifiersOnce();
    const container = this.prepareContainer();
    const fragment = this.serializeFragment(this.editorState.doc.content);
    container.appendChild(fragment);
    this.postprocessDomElement(container);
    return container.outerHTML;
  }

  /**
   * Add default modifiers if they have not been added to this instance
   * already. Automatically invoked by the `toHTML()` method.
   */
  addDefaultModifiersOnce() {
    if (!this.defaultsAdded) {
      this.addDefaultModifiers();
      this.defaultsAdded = true;
    }
  }

  /**
   * Add common modifiers to the serializer.
   *
   * We have a special rule for paragraph nodes that must be followed, so
   * it's implemented here. Override this method if you don't need this
   * behavior in your case.
   */
  addDefaultModifiers() {
    this.addNodeModifier('paragraph', (node, initialSpec) => {
      if (Array.isArray(initialSpec)) {
        var _node$textContent;
        for (let i = 0; i < node.childCount; i++) {
          const child = node.child(i);
          if (child.type.name !== 'text') {
            return initialSpec;
          }
        }
        if (((_node$textContent = node.textContent) === null || _node$textContent === void 0 ? void 0 : _node$textContent.trim()) === '') {
          return ['p', initialSpec[1], ['br', {
            class: 'hs-trailingbreak'
          }]];
        }
      }
      return initialSpec;
    });
  }

  /**
   * Return a DOM element to be used as the wrapper for document contents.
   *
   * @returns HTMLDivElement
   */
  prepareContainer() {
    const div = document.createElement('div');
    const fontPluginState = fontPluginKey.getState(this.editorState);
    if (fontPluginState && fontPluginState.defaultFontInfo) {
      const {
        defaultFontSize,
        defaultFontStyle,
        defaultTextColor
      } = fontPluginState.defaultFontInfo;
      div.setAttribute('style', createDefaultFontStyleString({
        defaultFontSize,
        defaultFontStyle,
        defaultTextColor
      }));
    }
    div.setAttribute('dir', 'auto');
    div.setAttribute('data-top-level', 'true');
    return div;
  }

  /**
   * Mutate the rendered DOM node before converting to HTML.
   *
   * @param el
   */
  postprocessDomElement(el) {
    const fontPluginState = fontPluginKey.getState(this.editorState);
    if (fontPluginState && fontPluginState.defaultFontInfo) {
      const {
        defaultFontSize
      } = fontPluginState.defaultFontInfo;
      if (defaultFontSize) {
        const signatureElements = el.querySelectorAll('.hs_signature');
        signatureElements.forEach(signatureDiv => {
          const elements = signatureDiv.querySelectorAll('p, div, span, h1, h2, h3, h4, h5, h6, li');
          elements.forEach(child => {
            const existingStyle = child.getAttribute('style') || '';
            const hasFontSize = /font-size\s*:/i.test(existingStyle);
            if (!hasFontSize) {
              const newStyle = `font-size: ${defaultFontSize}${existingStyle ? `; ${existingStyle}` : ''}`;
              child.setAttribute('style', newStyle);
            }
          });
        });
      }
    }
    el.querySelectorAll('[data-pm-output="none"]').forEach(node => {
      node.remove();
    });
  }
}